/**
 * clip-v2: stop-the-scroll social clip (9:16 + 1:1).
 *
 * HOOK → SETUP → TWIST → PAYOFF → CTA → END CARD, four formats
 * (why-it-lost / compare-two / change-one / explainer), optional voiceover,
 * music (ducked under voice), SFX cues and presenter clip. Everything is
 * driven by validated props under inputProps.clip (see ../CLIP_V2_CONTRACT.md).
 */
import React, { useMemo } from "react";
import { AbsoluteFill, Audio, OffthreadVideo, Sequence, interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { getAudioDurationInSeconds } from "@remotion/media-utils";
import { VIDEO_FONT_FAMILY } from "../../RegisterVideoFonts";
import { clampDurationInFrames, clampFps } from "../../lib/renderLimits";
import { resolveActiveWordIndex } from "../../lib/captionLogic";
import { resolveCaptionChunk } from "../scenarioExplainerTimeline";
import { parseClipV2InputProps } from "./clipV2Schema";
import {
  buildClipV2CaptionChunks,
  buildClipV2CaptionWords,
  buildClipV2Timeline,
  buildSfxCues,
  musicVolumeAt,
  resolveVoiceoverSeconds,
  smartQuotes,
  voiceRanges,
} from "./clipV2Timeline";
import { getClipV2Layout } from "./clipV2Layout";
import { MOTION, Slab, clamp, lerp, popIn, sp } from "./ClipV2Primitives";
import { CtaScene, EndScene, HookScene, PayoffScene, SetupScene, ThumbnailScene, TopRow } from "./ClipV2Scenes";
import { TwistScene } from "./ClipV2Twists";

// ── Metadata ────────────────────────────────────────────────

/** Validates props (throws → render fails), resolves narration length, computes duration. */
export async function calculateClipV2Metadata({ props }) {
  const parsed = parseClipV2InputProps(props);
  const fps = clampFps(parsed.fps ?? 30);
  const vo = parsed.voiceover;
  let probed = 0;
  if (vo && !vo.durationSeconds && !vo.words.length) {
    // No timing metadata: measure the file. Unreachable audio fails the render.
    probed = await getAudioDurationInSeconds(vo.audioUrl);
  }
  const voiceoverSeconds = resolveVoiceoverSeconds(vo, probed);
  const clip = {
    ...parsed,
    fps,
    voiceover: vo ? { ...vo, durationSeconds: voiceoverSeconds ? Math.min(voiceoverSeconds, 52) : null } : null,
  };
  const timeline = buildClipV2Timeline(clip, fps, voiceoverSeconds);
  return {
    durationInFrames: clampDurationInFrames(timeline.totalFrames, fps),
    fps,
    props: { ...props, clip },
  };
}

export async function calculateClipV2ThumbnailMetadata({ props }) {
  const clip = parseClipV2InputProps(props);
  return { durationInFrames: 1, props: { ...props, clip } };
}

function useClipV2Model(rawProps) {
  const { fps, width, height } = useVideoConfig();
  return useMemo(() => {
    const clip = parseClipV2InputProps(rawProps);
    const voiceoverSeconds = resolveVoiceoverSeconds(clip.voiceover);
    const timeline = buildClipV2Timeline(clip, fps, voiceoverSeconds);
    const words = buildClipV2CaptionWords(clip, timeline);
    const chunks = buildClipV2CaptionChunks(words);
    const layout = getClipV2Layout(width, height);
    return { clip, timeline, words, chunks, layout, voiceoverSeconds };
  }, [rawProps, fps, width, height]);
}

// ── Chrome: background, progress, swipe, captions ───────────

/** Flat background + drifting dot grid (no gradients, per brand rules). */
function Background({ colors, frame, totalFrames, layout }) {
  const { s } = layout;
  const cell = s(54);
  const phase = (frame / Math.max(1, totalFrames)) * Math.PI * 2;
  const dx = Math.sin(phase) * cell * 0.5;
  const dy = (frame * 0.35) % cell;
  return (
    <AbsoluteFill style={{ backgroundColor: colors.background }}>
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <pattern id="clipv2-dots" width={cell} height={cell} patternUnits="userSpaceOnUse" patternTransform={`translate(${dx} ${dy})`}>
            <circle cx={cell / 2} cy={cell / 2} r={s(2.6)} fill={colors.muted} fillOpacity={0.16} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#clipv2-dots)" />
      </svg>
    </AbsoluteFill>
  );
}

function ProgressBar({ colors, frame, totalFrames, height }) {
  return (
    <div style={{ position: "absolute", left: 0, right: 0, top: 0, height, background: colors.surface }}>
      <div style={{ width: `${Math.min(100, (frame / Math.max(1, totalFrames - 1)) * 100)}%`, height: "100%", background: colors.primary }} />
    </div>
  );
}

/** Solid panel sweeping across the frame on scene changes (hides the cut). */
function Swipes({ scenes, frame, colors }) {
  const span = 9;
  return scenes.slice(1).map((scene) => {
    const p = (frame - (scene.from - Math.floor(span / 2))) / span;
    if (p <= 0 || p >= 1) return null;
    const x = lerp(-130, 130, p);
    return (
      <div
        key={`swipe-${scene.from}`}
        style={{
          position: "absolute",
          top: "-20%",
          bottom: "-20%",
          left: "20%",
          width: "60%",
          background: colors.primary,
          transform: `translateX(${x}%) skewX(-14deg)`,
        }}
      />
    );
  });
}

function Captions({ words, chunks, colors, layout }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { s, L, captions, sidePad } = layout;
  const ms = (frame / fps) * 1000;
  const chunk = resolveCaptionChunk(chunks, ms, 350);
  if (!chunk) return null;
  const activeIndex = resolveActiveWordIndex(words, ms);
  const fontSize = s(L.captionFont);
  const startFrame = Math.round((chunk.startMs / 1000) * fps);
  const t = sp(frame, fps, MOTION.slam, startFrame);
  return (
    <div
      style={{
        position: "absolute",
        left: sidePad,
        right: sidePad,
        top: captions.top,
        height: captions.height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          transform: `scale(${lerp(0.82, 1, t)})`,
          opacity: interpolate(frame - startFrame, [0, 2], [0, 1], clamp),
          textAlign: "center",
          fontFamily: VIDEO_FONT_FAMILY,
          fontSize,
          fontWeight: 900,
          lineHeight: 1.16,
          letterSpacing: "-0.02em",
          color: colors.text,
          textShadow: `0 ${s(4)}px 0 rgba(0,0,0,0.55)`,
          textWrap: "balance",
          maxHeight: fontSize * 1.16 * 2 + s(8),
          overflow: "hidden",
        }}
      >
        {chunk.words.map((word, i) => {
          const active = word.globalIndex === activeIndex;
          return (
            <React.Fragment key={`${word.globalIndex}-${word.text}`}>
              {i > 0 ? " " : null}
              {active ? (
                <Slab colors={colors} fontSize={fontSize} style={{ textShadow: "none" }}>
                  {smartQuotes(word.text)}
                </Slab>
              ) : (
                <span>{smartQuotes(word.text)}</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Presenter({ presenter, colors, layout, fps }) {
  const { s, L, stage, sidePad, width } = layout;
  const frames = Math.max(1, Math.round((presenter.endSec - presenter.startSec) * fps));
  const video = (
    <OffthreadVideo
      src={presenter.videoUrl}
      trimBefore={Math.round(presenter.startSec * fps)}
      trimAfter={Math.round(presenter.endSec * fps)}
      muted={presenter.muted}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
  if (presenter.placement === "full") {
    return (
      <Sequence durationInFrames={frames} name="Presenter">
        <AbsoluteFill>{video}</AbsoluteFill>
        <AbsoluteFill style={{ backgroundColor: colors.background, opacity: 0.5 }} />
      </Sequence>
    );
  }
  const w = s(L.pipWidth);
  const h = s(L.pipHeight);
  return (
    <Sequence durationInFrames={frames} name="Presenter">
      <PipFrame left={width - sidePad - w} top={stage.top} w={w} h={h} colors={colors} frames={frames} s={s}>
        {video}
      </PipFrame>
    </Sequence>
  );
}

function PipFrame({ left, top, w, h, colors, frames, s, children }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const out = interpolate(frame, [frames - 6, frames], [1, 0], clamp);
  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: w,
        height: h,
        borderRadius: s(28),
        overflow: "hidden",
        border: `${s(6)}px solid ${colors.primary}`,
        opacity: out,
        ...popIn(frame, fps, 0, { from: 0.7, config: MOTION.bounce }),
      }}
    >
      {children}
    </div>
  );
}

function SfxTrack({ sfx, cues, fps }) {
  if (!sfx) return null;
  const len = Math.round(fps * 1.5);
  return (
    <>
      {sfx.whooshUrl
        ? cues.whoosh.map((f, i) => (
            <Sequence key={`w-${i}`} from={f} durationInFrames={len} name="SFX whoosh">
              <Audio src={sfx.whooshUrl} volume={0.45} />
            </Sequence>
          ))
        : null}
      {sfx.hitUrl
        ? cues.hit.map((f, i) => (
            <Sequence key={`h-${i}`} from={f} durationInFrames={len} name="SFX hit">
              <Audio src={sfx.hitUrl} volume={0.6} />
            </Sequence>
          ))
        : null}
    </>
  );
}

// ── Scene host ──────────────────────────────────────────────

function SceneBox({ top, height, layout, reserveRight = 0, children }) {
  return (
    <div style={{ position: "absolute", top, height, left: layout.sidePad, right: layout.sidePad + reserveRight }}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>{children}</div>
    </div>
  );
}

function SceneRenderer({ which, clip, layout, scene, shots }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const relShots = shots.map((s) => ({
    ...s,
    from: s.from - scene.from,
    shotFrom: s.shotFrom - scene.from,
    hitFrame: s.hitFrame == null ? null : s.hitFrame - scene.from,
  }));
  const common = { clip, layout, frame, fps, durationInFrames: scene.durationInFrames };
  const hitFrame = relShots[0]?.hitFrame ?? 10;
  switch (which) {
    case "hook":
      return <HookScene {...common} />;
    case "setup":
      return <SetupScene {...common} />;
    case "twist":
      return <TwistScene {...common} shots={relShots} />;
    case "payoff":
      return <PayoffScene {...common} hitFrame={hitFrame} />;
    case "cta":
      return <CtaScene {...common} />;
    case "end":
      return <EndScene {...common} />;
    default:
      return null;
  }
}

/** Micro punch-in on every split part of non-twist scenes (keeps a change every <= 3 s). */
function PartCamera({ shots, sceneFrom, children }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  let shot = shots[0];
  shots.forEach((s) => {
    if (frame + sceneFrom >= s.from) shot = s;
  });
  const f = frame + sceneFrom - shot.from;
  const base = shot.part % 2 === 1 ? 1.05 : 1;
  const scale = shot.part > 0 ? base * (1 + 0.04 * (1 - sp(f, fps, MOTION.punch))) : 1;
  return <div style={{ position: "absolute", inset: 0, transform: `scale(${scale})`, transformOrigin: "50% 50%" }}>{children}</div>;
}

export function ClipV2(rawProps) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { clip, timeline, words, chunks, layout, voiceoverSeconds } = useClipV2Model(rawProps);
  const { colors } = clip.brand;
  const { totalFrames } = timeline;
  const cues = useMemo(() => buildSfxCues(timeline), [timeline]);
  const ranges = useMemo(() => voiceRanges(clip, voiceoverSeconds), [clip, voiceoverSeconds]);

  // Camera drift: periodic over the whole clip, so the last frame lines up with the first (loop).
  const phase = (frame / Math.max(1, totalFrames)) * Math.PI * 2;
  const drift = {
    transform: `translate(${Math.sin(phase * 2) * layout.s(7)}px, ${Math.sin(phase * 3) * layout.s(5)}px) scale(${1.012 + 0.008 * Math.sin(phase * 2)})`,
    transformOrigin: "50% 45%",
  };

  const scene = (name) => timeline.scenes.find((x) => x.scene === name);
  const sceneShots = (name) => timeline.shots.filter((x) => x.scene === name);
  const header = { top: layout.header.top, height: layout.stage.top + layout.stage.height - layout.header.top };
  // Hook / end card use the full text band unless voiceover captions need the bottom.
  const boxFor = (name) =>
    name === "end" || (name === "hook" && !clip.voiceover) ? layout.band : name === "hook" ? header : layout.stage;
  const setup = scene("setup");
  const end = scene("end");
  const headerVisible = frame >= setup.from && frame < end.from;
  const pipFrames =
    clip.presenter?.placement === "pip" ? Math.round((clip.presenter.endSec - clip.presenter.startSec) * fps) : 0;
  const pipReserve = layout.s(layout.L.pipWidth) + layout.s(24);

  return (
    <AbsoluteFill style={{ fontFamily: VIDEO_FONT_FAMILY, overflow: "hidden" }}>
      {clip.voiceover ? <Audio src={clip.voiceover.audioUrl} /> : null}
      {clip.music ? (
        <Audio
          src={clip.music.audioUrl}
          loop
          volume={(f) =>
            musicVolumeAt(f, {
              fps,
              totalFrames,
              volume: clip.music.volume,
              duck: clip.music.duckUnderVoice,
              ranges,
            })
          }
        />
      ) : null}
      <SfxTrack sfx={clip.sfx} cues={cues} fps={fps} />

      <Background colors={colors} frame={frame} totalFrames={totalFrames} layout={layout} />
      {clip.presenter ? <Presenter presenter={clip.presenter} colors={colors} layout={layout} fps={fps} /> : null}

      <AbsoluteFill style={drift}>
        {headerVisible ? (
          <div style={{ position: "absolute", top: layout.header.top, left: layout.sidePad, right: layout.sidePad, height: layout.header.height }}>
            <TopRow clip={clip} layout={layout} style={{ height: "100%" }} />
          </div>
        ) : null}
        {timeline.scenes.map((sc) => {
          const box = boxFor(sc.scene);
          // A picture-in-picture presenter keeps its own column: scenes it overlaps get narrower.
          const reserve = pipFrames > 0 && sc.from < pipFrames ? pipReserve : 0;
          const sceneLayout = reserve
            ? { ...layout, box, contentWidth: layout.contentWidth - reserve, reserveRight: reserve }
            : { ...layout, box };
          return (
            <Sequence key={sc.scene} from={sc.from} durationInFrames={sc.durationInFrames} name={sc.scene} layout="none">
              <SceneBox top={box.top} height={box.height} layout={layout} reserveRight={reserve}>
                <PartCamera shots={sceneShots(sc.scene)} sceneFrom={sc.from}>
                  <SceneRenderer which={sc.scene} clip={clip} layout={sceneLayout} scene={sc} shots={sceneShots(sc.scene)} />
                </PartCamera>
              </SceneBox>
            </Sequence>
          );
        })}
      </AbsoluteFill>

      <Captions words={words} chunks={chunks} colors={colors} layout={layout} />
      <Swipes scenes={timeline.scenes} frame={frame} colors={colors} />
      {clip.progressBar ? <ProgressBar colors={colors} frame={frame} totalFrames={totalFrames} height={layout.s(layout.L.progressHeight)} /> : null}
    </AbsoluteFill>
  );
}

/** Thumbnail: the hook frame with the clip's big number. */
export function ClipV2Thumbnail(rawProps) {
  const { clip, layout } = useClipV2Model(rawProps);
  const box = layout.band;
  return (
    <AbsoluteFill style={{ fontFamily: VIDEO_FONT_FAMILY }}>
      <Background colors={clip.brand.colors} frame={0} totalFrames={1} layout={layout} />
      <SceneBox top={box.top} height={box.height} layout={layout}>
        <ThumbnailScene clip={clip} layout={layout} />
      </SceneBox>
    </AbsoluteFill>
  );
}
